<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">{{ $t('dmp.remoteOptions.title') }}</h5>
          <div class="d-flex gap-1">
            <b-button
              v-if="!isTemplateMode"
              variant="primary"
              size="sm"
              @click="saveRemoteOptions"
              :disabled="isSaving || isLoading"
            >
              <b-spinner v-if="isSaving" small class="mr-25" />
              <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
              {{ isSaving ? $t('actions.saving') : $t('actions.save') }}
            </b-button>
          </div>
        </div>

        <!-- Loading spinner -->
        <div v-if="isLoading" class="d-flex justify-content-center p-3">
          <b-spinner variant="primary" :label="$t('dmp.remoteOptions.loadingRemoteOptions')" />
          <span class="ml-2">{{ $t('dmp.remoteOptions.loadingRemoteOptions') }}</span>
        </div>

        <!-- Tabs for Standard and Entré -->
        <b-tabs v-else pills class="remote-options-tabs" v-model="tabIndex">
          <!-- Standard Tab -->
          <b-tab :title="$t('dmp.remoteOptions.standard')" active>
            <StandardTab
              ref="standardTabRef"
              :remote-options="remoteOptions"
              :yes-no-options="yesNoOptions"
              :entre-connection-options="entreConnectionOptions"
              :send-local-changes-options="sendLocalChangesOptions"
              :entre-connection-is-none="entreConnectionIsNone"
              :entre-connection-is-network="entreConnectionIsNetwork"
              :remote-change-ip-enabled="remoteChangeIpEnabled"
              :remote-change-port-enabled="remoteChangePortEnabled"
              :remote-phone-number-enabled="remotePhoneNumberEnabled"
              @save="saveRemoteOptions"
            />
          </b-tab>

          <!-- Entré Tab -->
          <b-tab :title="$t('dmp.remoteOptions.entre')">
            <EntreTab
              ref="entreTabRef"
              :remote-options="remoteOptions"
              :entre-connection-options="entreConnectionOptions"
              :entre-connection-fields-enabled="entreConnectionFieldsEnabled"
              :send-local-changes-is-none="sendLocalChangesIsNone"
              :send-local-changes-is-network="sendLocalChangesIsNetwork"
              :send-local-changes-is-dd="sendLocalChangesIsDD"
              :remote-change-ip-enabled="remoteChangeIpEnabled"
              :remote-change-port-enabled="remoteChangePortEnabled"
              :remote-phone-number-enabled="remotePhoneNumberEnabled"
              :yes-no-options="yesNoOptions"
              :send-local-changes-options="sendLocalChangesOptions"
              @save="saveRemoteOptions"
            />
          </b-tab>
        </b-tabs>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted, onBeforeUnmount, inject } from "@vue/composition-api";
import StandardTab from "./Standard.vue";
import EntreTab from "./Entre.vue";
import useConfigRemoteOptions from "./useConfigRemoteOptions";

export default {
  name: "ConfigRemoteOptions",
  components: {
    StandardTab,
    EntreTab,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    remoteOptionsData: {
      type: Array,
      default: () => [],
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const uuid = computed(() => props.uuid);
    const remoteOptionsData = computed(() => props.remoteOptionsData);
    const tabIndex = ref(0); // 0 = Standard, 1 = Entré
    const standardTabRef = ref(null);
    const entreTabRef = ref(null);

    const {
      isLoading,
      isSaving,
      remoteOptions,
      yesNoOptions,
      sendLocalChangesOptions,
      entreConnectionOptions,
      activeTab,
      entreConnectionIsNone,
      entreConnectionIsNetwork,
      sendLocalChangesIsNone,
      sendLocalChangesIsNetwork,
      sendLocalChangesIsDD,
      entreConnectionFieldsEnabled,
      remoteChangeIpEnabled,
      remoteChangePortEnabled,
      remotePhoneNumberEnabled,
      standardFormObserver,
      entreFormObserver,
      loadRemoteOptions,
      saveRemoteOptions,
      syncRemoteOptions,
      isTemplateMode,
      buildTemplateItem,
      validateTemplateSection,
      t
    } = useConfigRemoteOptions(uuid, remoteOptionsData, emit, props);

    const registerTemplateSection = inject("registerTemplateSection", null);
    const unregisterTemplateSection = inject("unregisterTemplateSection", null);

    onMounted(() => {
      if (isTemplateMode.value && registerTemplateSection) {
        registerTemplateSection("remote_options", {
          validate: validateTemplateSection,
          collect: buildTemplateItem,
        });
      }
    });

    onBeforeUnmount(() => {
      if (isTemplateMode.value && unregisterTemplateSection) {
        unregisterTemplateSection("remote_options");
      }
    });

    watch(tabIndex, (newValue) => {
      activeTab.value = newValue === 0 ? "standard" : "entre";
    });

    // Sync form observers with child component refs
    watch(standardTabRef, (newRef) => {
      if (newRef && newRef.formObserver) {
        standardFormObserver.value = newRef.formObserver;
      }
    });

    watch(entreTabRef, (newRef) => {
      if (newRef && newRef.formObserver) {
        entreFormObserver.value = newRef.formObserver;
      }
    });

    watch(
      () => props.remoteOptionsData,
      (newData) => {
        if (newData) {
          loadRemoteOptions();
        }
      },
      { deep: true }
    );

    return {
      isLoading,
      isSaving,
      remoteOptions,
      yesNoOptions,
      sendLocalChangesOptions,
      entreConnectionOptions,
      tabIndex,
      standardTabRef,
      entreTabRef,
      entreConnectionIsNone,
      entreConnectionIsNetwork,
      sendLocalChangesIsNone,
      sendLocalChangesIsNetwork,
      sendLocalChangesIsDD,
      entreConnectionFieldsEnabled,
      remoteChangeIpEnabled,
      remoteChangePortEnabled,
      remotePhoneNumberEnabled,
      loadRemoteOptions,
      saveRemoteOptions,
      isTemplateMode,
    };
  },
};
</script>

<style scoped lang="scss">
.remote-options-tabs {
  ::v-deep .nav-pills {
    .nav-link {
      padding: 0.5rem 1rem;
    }
  }
}
</style>
